import { useQuery, useQueryClient } from "@tanstack/react-query"
import notification from "common/utils/notification"
import { useTranslation } from "react-i18next"
import { useParams } from "react-router-dom"
import { useSupportedBanksFromAfterbanks } from "./Afterbanks.hook"
import {
  ByFilterSimpleContactosQueryParamsProps,
  ContactoEtiquetasQueryParamsProps,
  deleteContactoCtasBancariasByEmpresaId,
  getContactoEtiquetas,
  getContactoSaldoPendiente,
  getContactosByFilterSimple,
  getContactosById,
  getCtasBancariasContactoById,
} from "./Contacto.service"

interface DeleteCuentasBancariaProps {
  onSuccess: () => void
}

export function useContactoSaldoPendiente(contactoId?: string) {
  const { data: saldoPendiente, isLoading: saldoPendienteIsLoading } = useQuery(
    {
      queryKey: ["getContactoSaldoPendiente", contactoId],
      queryFn: () => {
        if (!contactoId) return Promise.resolve({ saldoCobro: 0, saldoPago: 0 })
        return getContactoSaldoPendiente(contactoId)
      },
      refetchOnWindowFocus: false,
      retry: false,
    }
  )

  return {
    saldoPendiente,
    saldoPendienteIsLoading,
  }
}

export function useContactosByFilterSimple(
  queryParams?: ByFilterSimpleContactosQueryParamsProps
) {
  const queryClient = useQueryClient()

  const {
    data: contactosByFilter,
    isLoading: contactosByFilterIsLoading,
    error,
    refetch: contactosByFilterRefetch,
  } = useQuery({
    queryKey: ["getContactosByFilterSimple", queryParams],
    queryFn: () => getContactosByFilterSimple({ queryParams }),
    refetchOnWindowFocus: false,
    retry: false,
  })
  const contactosByFilterSimpleInvalidation = () =>
    queryClient.invalidateQueries({ queryKey: ["getContactosByFilterSimple"] })

  return {
    contactosByFilter,
    contactosByFilterIsLoading,
    error,
    contactosByFilterRefetch,
    contactosByFilterSimpleInvalidation,
  }
}

export function useContactosCuentaBancariaById(id: string) {
  const { supportedBanks } = useSupportedBanksFromAfterbanks("ES")

  const {
    data: contactosCuentaBancariaById,
    isLoading: contactosCuentaBancariaByIdIsLoading,
    error: contactosCuentaBancariaByIdError,
    refetch: contactosCuentaBancariaByIdRefetch,
  } = useQuery({
    queryKey: ["getCtasBancariasContactoById", id],
    queryFn: () => getCtasBancariasContactoById(id),
    select: (data) =>
      data.map((cta) => {
        const bancoInfo = supportedBanks?.find((bank) => bank.swift === cta.bic)

        return {
          ...cta,
          logo: bancoInfo?.image,
        }
      }),
    refetchOnWindowFocus: true,
    retry: false,
  })
  return {
    contactosCuentaBancariaById,
    contactosCuentaBancariaByIdIsLoading,
    contactosCuentaBancariaByIdError,
    contactosCuentaBancariaByIdRefetch,
  }
}

export const useDeleteCuentaBancaria = ({
  onSuccess,
}: DeleteCuentasBancariaProps) => {
  const { t } = useTranslation()
  const { id } = useParams<{ id: string }>()
  return async (cuentaId: string) => {
    try {
      if (!id) return
      await deleteContactoCtasBancariasByEmpresaId(id, cuentaId)
      notification.success({
        body: `${t("contactos.cuentasBancarias.deleteSuccess")}`,
      })
      onSuccess()
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error al eliminar la cuenta bancaria:", error)
    }
  }
}

export const useContactosEtiquetas = (
  queryParams?: ContactoEtiquetasQueryParamsProps
) => {
  const queryClient = useQueryClient()

  const {
    data: contactosEtiquetas,
    isLoading: contactosEtiquetasIsLoading,
    error,
    refetch: contactosEtiquetasRefetch,
  } = useQuery({
    queryKey: ["contactosEtiquetas", queryParams],
    queryFn: () => getContactoEtiquetas(queryParams),
    refetchOnWindowFocus: false,
    retry: false,
  })
  const contactosEtiquetasInvalidation = () =>
    queryClient.invalidateQueries({ queryKey: ["contactosEtiquetas"] })

  return {
    contactosEtiquetas,
    contactosEtiquetasIsLoading,
    error,
    contactosEtiquetasRefetch,
    contactosEtiquetasInvalidation,
  }
}

export const useContactosById = (id?: string) => {
  const queryClient = useQueryClient()

  const { data: contactoById } = useQuery({
    queryKey: ["contactoById", id],
    enabled: Boolean(id),
    queryFn: () => getContactosById(id ?? ""),
    refetchOnWindowFocus: false,
    retry: false,
    retryOnMount: false,
  })

  const contactosByIdInvalidation = () =>
    queryClient.invalidateQueries({ queryKey: ["contactoById"] })

  return {
    contactoById,
    contactosByIdInvalidation,
  }
}
